/**
 * The composer's model control: the current model's short name, and a menu of
 * the catalogue grouped as the prototype groups it.
 *
 * Two groups on the web, not three. "On this device" is Ollama and LM Studio,
 * which only the desktop app can reach (CLAUDE.md: local providers are the
 * desktop app's), so `device` models are never offered here even if the
 * catalogue lists them.
 *
 * Picking a third-party model while `warnThirdParty` is on asks first, with the
 * prototype's dialog. Any pick also becomes the account's default, so the next
 * new chat starts on the model the person last chose.
 *
 * On a phone the effort rides here too (`effort`): the chip reads
 * "gpt-oss · Medium", and the menu ends with the effort popover's body, held
 * below the list so a long catalogue does not scroll it away.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DEFAULT_EFFORT } from '@wire';
import { patchSettings, type Effort, type ModelGroup, type ModelInfo, type ProviderKey } from '../../api';
import { ds } from '../../ds';
import { useRouter } from '../../router';
import { useApp } from '../../state';
import { Menu, MenuDivider, MenuItem, MonoLabel, Ph, useDismiss } from '../ui';
import { modelEffortLabel, nearestEffort } from './effort';
import { EffortPanel, useEffortPick } from './EffortMenu';

const GROUPS: { group: ModelGroup; label: string }[] = [
  { group: 'private', label: 'Private' },
  { group: 'third', label: 'Third-party' }
];

/** Who a third-party model's chats go to, for the warning. The catalogue's
 *  `meta` usually says; this covers a model that came without one. */
const PROVIDER_NAMES: Partial<Record<ProviderKey, string>> = {
  google: 'Google',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  openrouter: 'OpenRouter'
};

export const modelIcon = (m: ModelInfo | null): { name: string; color: string } =>
  m?.group === 'private'
    ? { name: 'shield-check', color: 'var(--signal)' }
    : { name: 'globe-simple', color: 'var(--text-muted)' };

/** The composer label for a model id, falling back to the id's last segment
 *  while the catalogue loads or when the model has left it. */
export function shortName(id: string, models: ModelInfo[] | undefined): string {
  const m = models?.find((x) => x.id === id);
  if (m) return m.short;
  const tail = id.slice(id.indexOf(':') + 1);
  return tail.slice(tail.lastIndexOf('/') + 1) || id;
}

export function ModelMenu({
  value,
  onPick,
  open,
  onToggle,
  onClose,
  effort
}: {
  value: string;
  onPick: (id: string) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** A phone's: the effort shown and picked from this menu. */
  effort?: { value: Effort; onPick: (effort: Effort) => void };
}) {
  const { me, catalogue, patchMe } = useApp();
  const { navigate } = useRouter();
  const ref = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useDismiss(ref, open, onClose);

  // On a phone the chip sits too far left for the menu to open leftwards from
  // it, so the menu moves right until it is on screen.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const over = 16 - el.getBoundingClientRect().left;
    if (over > 0) el.style.right = `${-over}px`;
    // The menu opens upward, so the room above the chip limits it, up to the
    // nearest ancestor that clips (the chat's scroller), not the viewport.
    let edge = 0;
    for (let a = ref.current?.parentElement; a; a = a.parentElement) {
      if (getComputedStyle(a).overflowY !== 'visible') {
        edge = Math.max(edge, a.getBoundingClientRect().top);
        break;
      }
    }
    const room = (ref.current?.getBoundingClientRect().top ?? Infinity) - edge - 6 - 8;
    if (room < el.getBoundingClientRect().height) el.style.maxHeight = `${Math.max(room, 120)}px`;
  }, [open]);

  const [more, setMore] = useState(false);
  const [pending, setPending] = useState<ModelInfo | null>(null);
  const [dontWarn, setDontWarn] = useState(false);

  const models = catalogue?.models ?? [];
  const current = models.find((m) => m.id === value) ?? null;
  const icon = modelIcon(current);

  const efforts = current?.efforts ?? [];
  const shownEffort = effort ? nearestEffort(effort.value, efforts) : null;
  // A hook, so called whether or not the effort rides here.
  const pickEffort = useEffortPick(effort?.value ?? DEFAULT_EFFORT, (e) => effort?.onPick(e));
  const label = modelEffortLabel(shortName(value, models), shownEffort);

  const commit = (m: ModelInfo, stopWarning: boolean) => {
    onPick(m.id);
    patchSettings({ defaultModel: m.id, ...(stopWarning ? { warnThirdParty: false } : {}) })
      .then((settings) => patchMe((x) => ({ ...x, settings })))
      .catch(() => {
        // The chat already has the model; only the default for the next one
        // did not stick, which is not worth interrupting anyone over.
      });
  };

  const pick = (m: ModelInfo) => {
    onClose();
    if (m.id === value) return;
    if (m.group === 'third' && me?.settings.warnThirdParty) {
      setDontWarn(false);
      setPending(m);
      return;
    }
    commit(m, false);
  };

  // The design system's Dialog closes on its scrim and its ×, not on Escape.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPending(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pending]);

  const featured = models.filter((m) => m.featured);
  // Open to everything when nothing is featured, or the pick is not.
  const showAll = !featured.length || more || (!!current && !current.featured);

  const row = (m: ModelInfo) => {
    const mi = modelIcon(m);
    return (
      <MenuItem
        key={m.id}
        icon={mi.name}
        iconColor={mi.color}
        label={m.name}
        meta={m.preview ? ['Preview', m.meta].filter(Boolean).join(' · ') : (m.meta ?? undefined)}
        selected={m.id === value}
        onSelect={() => pick(m)}
        style={{ minHeight: 36 }}
      />
    );
  };

  const { Dialog, Button, Checkbox } = ds();

  return (
    <span ref={ref} style={{ position: 'relative', display: 'flex', minWidth: 0 }}>
      <button
        type="button"
        className="bare hov-tint"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Model: ${label}`}
        onClick={onToggle}
        style={{
          height: 32,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 8px',
          borderRadius: 'var(--radius-sm)',
          fontSize: 12.5,
          color: 'var(--text-body)',
          whiteSpace: 'nowrap'
        }}
      >
        <Ph name={icon.name} size={14} color={icon.color} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <Ph name="caret-down" size={11} color="var(--text-faint)" />
      </button>

      {open ? (
        <Menu
          ref={menuRef}
          style={{
            right: 0,
            bottom: 'calc(100% + 6px)',
            width: 280,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(440px, 62vh)',
            overflow: 'auto'
          }}
        >
          {featured.length ? (
            <>
              <MonoLabel style={{ padding: '8px 9px 4px' }}>Featured</MonoLabel>
              {featured.map(row)}
            </>
          ) : null}
          {featured.length && !showAll ? (
            <MenuItem
              icon="caret-down"
              label={`More models (${models.length - featured.length})`}
              onSelect={() => setMore(true)}
              style={{ color: 'var(--text-muted)' }}
            />
          ) : null}
          {showAll
            ? GROUPS.map(({ group, label }) => {
                const items = models.filter((m) => m.group === group && !(featured.length && m.featured));
                if (!items.length) return null;
                return (
                  <React.Fragment key={group}>
                    <MonoLabel style={{ padding: '8px 9px 4px' }}>{featured.length ? `More · ${label}` : label}</MonoLabel>
                    {items.map(row)}
                  </React.Fragment>
                );
              })
            : null}
          <MenuDivider />
          <MenuItem
            icon="sliders-horizontal"
            label="Manage models"
            onSelect={() => {
              onClose();
              navigate('/settings/models');
            }}
            style={{ color: 'var(--text-muted)' }}
          />
          {shownEffort ? (
            <div
              style={{
                position: 'sticky',
                bottom: -5,
                margin: '5px -5px -5px',
                padding: '12px 14px',
                borderTop: '1px solid var(--border-subtle)',
                background: 'var(--surface-card)'
              }}
            >
              <EffortPanel offered={efforts} value={shownEffort} onChange={pickEffort} />
            </div>
          ) : null}
        </Menu>
      ) : null}

      {pending ? (
        <Dialog
          open
          position="fixed"
          width={440}
          title={`Use ${pending.name}?`}
          onClose={() => setPending(null)}
          style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 14 }}
          footer={
            <>
              <Button variant="ghost" density="app" size="sm" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                density="app"
                size="sm"
                onClick={() => {
                  commit(pending, dontWarn);
                  setPending(null);
                }}
              >
                Use this model
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-body)' }}>
              {pending.provider === 'ollama' ? (
                <>Chats with this model are sent to the Ollama server at {pending.meta ?? 'the address you gave'}.</>
              ) : (
                <>
                  Chats with this model are sent to{' '}
                  {pending.meta ?? PROVIDER_NAMES[pending.provider] ?? 'the provider'} and covered by their privacy
                  policy. We can't guarantee they aren't used for training.
                </>
              )}
            </p>
            <Checkbox label="Don't warn me again" checked={dontWarn} onChange={(v) => setDontWarn(v)} />
          </div>
        </Dialog>
      ) : null}
    </span>
  );
}
